import { supabase } from '@/shared/config/supabase';
import {
  IRankingRepository,
  GetLeaderboardParams,
  AddXPParams,
} from '../../domain/repositories/IRankingRepository';
import { StudentRanking } from '../../domain/entities/StudentRanking';
import { LeaderboardEntry } from '../../domain/entities/LeaderboardEntry';
import { DailyStreak } from '../../domain/entities/DailyStreak';
import { XPBoost } from '../../domain/entities/XPBoost';
import { WeeklyCycle } from '../../domain/entities/WeeklyCycle';
import { XPTransaction } from '../../domain/entities/XPTransaction';
import { LeagueTier } from '../../domain/value-objects/LeagueTier';
import { XPEventType } from '../../domain/value-objects/XPEventType';
import { PromotionStatus } from '../../domain/value-objects/PromotionStatus';
import { BOOST_DURATION_MINUTES } from '../../domain/value-objects/BoostStatus';

export class SupabaseRankingRepository implements IRankingRepository {
  async getStudentRanking(studentId: string): Promise<StudentRanking | null> {
    // Get user's workspace
    const { data: user } = await supabase
      .from('users')
      .select('workspace_id')
      .eq('id', studentId)
      .single();

    if (!user) return null;

    // Get current weekly cycle
    const { data: cycle } = await supabase
      .rpc('get_current_weekly_cycle', { p_workspace_id: user.workspace_id })
      .maybeSingle() as unknown as { data: { cycle_id: number; cycle_start_date: string; cycle_end_date: string } | null };

    if (!cycle) return null;

    // Get participant data with league info
    const { data: participant } = await supabase
      .from('LeagueParticipants')
      .select(`
        *,
        Leagues!inner(id, tier, group_number),
        users!inner(full_name, avatar, email, lifetime_xp, inactive_cycles)
      `)
      .eq('student_id', studentId)
      .eq('weekly_cycle_id', cycle.cycle_id)
      .maybeSingle();

    if (!participant) return null;

    // Get streak data
    const { data: streak } = await supabase
      .from('DailyStreaks')
      .select('*')
      .eq('student_id', studentId)
      .maybeSingle();

    // Count activities
    const { data: activities } = await supabase
      .from('XPEvents')
      .select('source')
      .eq('student_id', studentId)
      .eq('weekly_cycle_id', cycle.cycle_id);

    const activityCounts = (activities || []).reduce(
      (acc, event) => {
        if (event.source === 'LESSON_COMPLETE') acc.lessons++;
        if (event.source === 'QUIZ_PASS' || event.source === 'QUIZ_PERFECT') acc.quizzes++;
        if (event.source === 'VIDEO_WATCH') acc.videos++;
        return acc;
      },
      { lessons: 0, quizzes: 0, videos: 0 }
    );

    const userName = participant.users.full_name || participant.users.email;

    return new StudentRanking({
      id: participant.id.toString(),
      studentId,
      studentName: userName,
      studentAvatar: participant.users.avatar,
      leagueId: participant.Leagues.id.toString(),
      tier: participant.Leagues.tier as LeagueTier,
      weekXP: participant.total_xp,
      totalXP: participant.total_xp,
      lifetimeXP: participant.users.lifetime_xp || 0,
      inactiveCycles: participant.users.inactive_cycles || 0,
      position: participant.current_position || 999,
      previousPosition: participant.previous_position,
      currentStreak: streak?.current_streak || 0,
      longestStreak: streak?.longest_streak || 0,
      lessonsCompleted: activityCounts.lessons,
      quizzesPassed: activityCounts.quizzes,
      videosWatched: activityCounts.videos,
      weeklyCycleId: cycle.cycle_id.toString(),
      weekStartDate: new Date(cycle.cycle_start_date),
      weekEndDate: new Date(cycle.cycle_end_date),
      updatedAt: new Date(participant.updated_at),
    });
  }

  async getLeaderboard(params: GetLeaderboardParams): Promise<LeaderboardEntry[]> {
    const { data: entries, error } = await supabase.rpc('get_league_leaderboard', {
      p_league_id: parseInt(params.leagueId),
      p_weekly_cycle_id: parseInt(params.weeklyCycleId),
    });

    if (error || !entries) {
      console.error('Error fetching leaderboard:', error);
      return [];
    }

    // Calculate XP differences
    const sortedEntries = entries.sort(
      (a: { total_xp: number }, b: { total_xp: number }) => b.total_xp - a.total_xp
    );

    return sortedEntries.map((entry: {
      rank_position: number;
      student_id: string;
      student_name: string;
      student_avatar: string | null;
      total_xp: number;
      current_streak: number;
      position_change: number;
      promotion_status: string;
    }, index: number) => {
      const xpDifferenceToAbove =
        index > 0 ? sortedEntries[index - 1].total_xp - entry.total_xp : undefined;
      const xpDifferenceToBelow =
        index < sortedEntries.length - 1
          ? entry.total_xp - sortedEntries[index + 1].total_xp
          : undefined;

      return new LeaderboardEntry({
        position: entry.rank_position,
        studentId: entry.student_id,
        studentName: entry.student_name || 'Usuario',
        studentAvatar: entry.student_avatar || undefined,
        weekXP: entry.total_xp,
        currentStreak: entry.current_streak,
        isCurrentUser: entry.student_id === params.currentUserId,
        promotionStatus: entry.promotion_status as PromotionStatus,
        positionChange: entry.position_change,
        xpDifferenceToAbove,
        xpDifferenceToBelow,
      });
    });
  }

  async getCurrentWeeklyCycle(workspaceId: number): Promise<WeeklyCycle | null> {
    const { data, error } = await supabase
      .rpc('get_current_weekly_cycle', { p_workspace_id: workspaceId })
      .maybeSingle() as unknown as { data: { cycle_id: number; cycle_start_date: string; cycle_end_date: string } | null; error: { message: string } | null };

    if (error || !data) {
      // Try to get from table directly
      const { data: cycleData } = await supabase
        .from('WeeklyCycles')
        .select('*')
        .eq('workspace_id', workspaceId)
        .eq('is_active', true)
        .gte('end_date', new Date().toISOString())
        .lte('start_date', new Date().toISOString())
        .maybeSingle();

      if (!cycleData) return null;

      return new WeeklyCycle({
        id: cycleData.id.toString(),
        workspaceId: cycleData.workspace_id,
        startDate: new Date(cycleData.start_date),
        endDate: new Date(cycleData.end_date),
        isActive: cycleData.is_active,
        createdAt: new Date(cycleData.created_at),
      });
    }

    return new WeeklyCycle({
      id: data.cycle_id.toString(),
      workspaceId,
      startDate: new Date(data.cycle_start_date),
      endDate: new Date(data.cycle_end_date),
      isActive: true,
      createdAt: new Date(),
    });
  }

  async getDailyStreak(studentId: string): Promise<DailyStreak | null> {
    const { data, error } = await supabase
      .from('DailyStreaks')
      .select('*')
      .eq('student_id', studentId)
      .maybeSingle();

    if (error || !data) return null;

    return new DailyStreak({
      id: data.id.toString(),
      studentId: data.student_id,
      currentStreak: data.current_streak,
      longestStreak: data.longest_streak,
      lastActivityDate: new Date(data.last_activity_date),
      updatedAt: new Date(data.updated_at),
    });
  }

  async getActiveBoost(studentId: string): Promise<XPBoost | null> {
    const { data, error } = await supabase
      .from('XPBoosts')
      .select('*')
      .eq('student_id', studentId)
      .eq('is_active', true)
      .gt('expires_at', new Date().toISOString())
      .order('created_at', { ascending: false })
      .limit(1)
      .maybeSingle();

    if (error || !data) return null;

    return new XPBoost({
      id: data.id.toString(),
      studentId: data.student_id,
      activatedAt: new Date(data.activated_at),
      expiresAt: new Date(data.expires_at),
      isActive: data.is_active,
      xpEarned: data.xp_earned || 0,
      createdAt: new Date(data.created_at),
    });
  }

  async hasUsedBoostToday(studentId: string): Promise<boolean> {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const { data } = await supabase
      .from('XPBoosts')
      .select('id')
      .eq('student_id', studentId)
      .gte('created_at', today.toISOString())
      .limit(1);

    return (data?.length || 0) > 0;
  }

  async activateBoost(studentId: string): Promise<XPBoost> {
    const now = new Date();
    const expiresAt = new Date(now.getTime() + BOOST_DURATION_MINUTES * 60 * 1000);

    const { data, error } = await supabase
      .from('XPBoosts')
      .insert({
        student_id: studentId,
        activated_at: now.toISOString(),
        expires_at: expiresAt.toISOString(),
        is_active: true,
        xp_earned: 0,
      })
      .select()
      .single();

    if (error) throw new Error(`Failed to activate boost: ${error.message}`);

    return new XPBoost({
      id: data.id.toString(),
      studentId: data.student_id,
      activatedAt: new Date(data.activated_at),
      expiresAt: new Date(data.expires_at),
      isActive: data.is_active,
      xpEarned: data.xp_earned || 0,
      createdAt: new Date(data.created_at),
    });
  }

  async addXP(params: AddXPParams): Promise<number> {
    const { data, error } = await supabase.rpc('add_student_xp', {
      p_student_id: params.studentId,
      p_source: params.source,
      p_base_xp: params.baseXP,
      p_related_entity_id: params.relatedEntityId
        ? parseInt(params.relatedEntityId)
        : null,
    });

    if (error) {
      console.error('Error adding XP:', error);
      return 0;
    }

    return data || 0;
  }

  async getRecentXPTransactions(
    studentId: string,
    limit: number = 10
  ): Promise<XPTransaction[]> {
    const { data, error } = await supabase
      .from('XPEvents')
      .select('*')
      .eq('student_id', studentId)
      .order('created_at', { ascending: false })
      .limit(limit);

    if (error || !data) return [];

    return data.map(
      (event) =>
        new XPTransaction({
          id: event.id.toString(),
          studentId: event.student_id,
          eventType: event.source as XPEventType,
          baseXP: event.base_xp,
          multiplier: parseFloat(event.multiplier),
          finalXP: event.final_xp,
          relatedEntityId: event.related_entity_id?.toString(),
          weeklyCycleId: event.weekly_cycle_id.toString(),
          createdAt: new Date(event.created_at),
        })
    );
  }

  async getXPBreakdown(
    studentId: string,
    weeklyCycleId: string
  ): Promise<Record<XPEventType, number>> {
    const { data, error } = await supabase
      .from('XPEvents')
      .select('source, final_xp')
      .eq('student_id', studentId)
      .eq('weekly_cycle_id', parseInt(weeklyCycleId));

    if (error || !data) {
      return {} as Record<XPEventType, number>;
    }

    return data.reduce(
      (acc, event) => {
        const source = event.source as XPEventType;
        acc[source] = (acc[source] || 0) + event.final_xp;
        return acc;
      },
      {} as Record<XPEventType, number>
    );
  }

  async getLeagueHistory(
    studentId: string,
    limit: number = 10
  ): Promise<
    Array<{
      weeklyCycleId: string;
      tier: LeagueTier;
      finalPosition: number;
      totalParticipants: number;
      weekXP: number;
      promoted: boolean;
      demoted: boolean;
      weekStartDate: Date;
      weekEndDate: Date;
    }>
  > {
    const { data, error } = await supabase
      .from('LeagueHistory')
      .select(
        `
        *,
        WeeklyCycles!inner(start_date, end_date)
      `
      )
      .eq('student_id', studentId)
      .order('created_at', { ascending: false })
      .limit(limit);

    if (error || !data) return [];

    return data.map((record) => ({
      weeklyCycleId: record.weekly_cycle_id.toString(),
      tier: record.league_tier as LeagueTier,
      finalPosition: record.final_position,
      totalParticipants: record.total_participants,
      weekXP: record.total_xp,
      promoted: record.promotion_status === 'PROMOTED',
      demoted: record.promotion_status === 'DEMOTED',
      weekStartDate: new Date(record.WeeklyCycles.start_date),
      weekEndDate: new Date(record.WeeklyCycles.end_date),
    }));
  }

  async ensureStudentParticipation(studentId: string): Promise<{
    leagueId: string;
    tier: LeagueTier;
    weeklyCycleId: string;
  }> {
    // Get user's workspace
    const { data: user } = await supabase
      .from('users')
      .select('workspace_id')
      .eq('id', studentId)
      .single();

    if (!user) throw new Error('User not found');

    // Use the SECURITY DEFINER function to handle cycle/league/participant creation
    const { data, error } = await supabase.rpc('ensure_student_participation', {
      p_student_id: studentId,
      p_workspace_id: user.workspace_id,
    });

    if (error) {
      throw new Error(`Failed to ensure participation: ${error.message}`);
    }

    if (!data || data.length === 0) {
      throw new Error('No participation data returned');
    }

    const result = data[0];
    return {
      leagueId: result.league_id.toString(),
      tier: result.tier as LeagueTier,
      weeklyCycleId: result.weekly_cycle_id.toString(),
    };
  }
}
